/* =============================================================================
 * COPY EDITORIAL DE /cancun — transcrito del mockup, con su procedencia
 * -----------------------------------------------------------------------------
 * Los DATOS (fechas, programa, hitos, claim manuscrito, venue, estado de venta)
 * salen de `@/features/events` y NO están aquí. Esto son los rótulos de sección
 * y la biografía que el mockup `DEMO_5_CancunVIP.png` rotula y que no tienen
 * campo en el dato. Ni precios, ni aforos, ni direcciones, ni testimonios.
 * ========================================================================== */

export const CANCUN_COPY = {
  vipLabel: 'VIP',
  /**
   * AFORO — 12, confirmado por el cliente el 26-ago-2026 («en la web de Cancún
   * poner y de forma resaltada "SOLO PARA 12 ASISTENTES"»).
   *
   * ESTO REVOCA la decisión del 26-jul que quitó «Lugares limitados» de la tira
   * premium (ver el comentario de `strip.claim` aquí abajo). Aquel bloqueo era
   * por la línea roja 6 de `CLAUDE.md` — «"Plazas restantes" NO (falsa
   * escasez)» — y el motivo REAL era que el aforo no estaba confirmado. Ya lo
   * está, y esto no es «plazas restantes»: es el aforo FIJO del formato, un
   * dato estático, de la misma familia que el countdown que la línea roja sí
   * permite por ser objetivo. Lo que sigue prohibido es un contador vivo de
   * plazas libres.
   *
   * CUADRA CON LA TIENDA, y esa es la comprobación que hace que sea un dato y
   * no un eslogan: el cupo «Plazas · Convivencia» de `cancun` en pretix es de
   * 12. Si alguien cambia el aforo allí, esta cifra miente — verificar contra
   * `/api/v1/organizers/azrahel/events/cancun/quotas/`.
   */
  aforo: 'Solo para 12 asistentes',
  programa: {
    eyebrow: 'Programa',
    subtitle: 'Ciclo vivencial en 3 días',
  },
  strip: {
    /**
     * Dos mitades para poder partirlas en móvil por el `·` del mockup.
     * DECISIÓN DEL ORACLE (20260726): la segunda mitad decía "Lugares
     * limitados", que ADR-003 §4 permitía por el formato petit comité. La
     * revoca `CLAUDE.md` línea roja 6 ("plazas restantes" = falsa escasez):
     * el aforo del retiro no está confirmado por el cliente, así que afirmar
     * escasez es una promesa comercial sin dato detrás. Se sustituye por la
     * misma formulación honesta que ya usa el hero: exclusividad sin cifra
     * ni urgencia inventada. Ver `docs/VISUAL_DEVIATIONS.md` §1.23.
     */
    claim: ['Experiencia premium', 'Comité íntimo'],
  },
  cierre: {
    name: 'Azrahel',
    role: ['Mentor', 'Maestro', 'Guía'],
    /**
     * DECISIÓN DEL ORACLE (20260725): el mockup escribe "en miles de almas
     * alrededor del mundo". Esa cifra no está confirmada por el cliente, así
     * que la bio se redacta SIN cifra. El resto es descripción del propio
     * mentor, sin sellos médicos ni promesas (ADR-003 §4).
     */
    bio: 'Más de tres décadas acompañando procesos de transformación y expansión de conciencia alrededor del mundo. Su enseñanza integra sabiduría ancestral, ciencia cuántica y experiencia vivencial.',
    legacy: ['Consciencia', 'Transformación', 'Legado'],
  },

  /* ---------------------------------------------------------------------------
   * ADICIONES EXIGIDAS POR EL BRIEF (ADR-006) — bloques que el mockup no dibuja
   * y que el master prompt §7 y `07_ARQUITECTURA_LANDINGS.md` §5-6 sí exigen.
   * Sólo rótulos: las preguntas y sus respuestas salen del dato tipado.
   * ------------------------------------------------------------------------ */
  social: {
    /* Encargo del cliente (28-jul-2026): «un título que diga algo como otros ya
     * han estado». Sin «en otras giras»: la de 2027 es la primera (ADR-003 §2). */
    eyebrow: 'Otros ya han estado antes',
    caption: 'Conferencia en Miami, 2023 · «Semillas Estelares»',
  },
  faq: {
    eyebrow: 'Antes de reservar',
    /**
     * El rótulo de sección era sólo «Antes de reservar», que es tono, no
     * etiqueta: quien busca la FAQ —o quien la audita— busca «preguntas
     * frecuentes» y no la encontraba escrita en ningún sitio de la página.
     * El tono se queda arriba como antetítulo; el titular dice qué es.
     */
    titulo: 'Preguntas frecuentes',
    /** Marca honesta de dato que todavía no existe (ADR-003 §1). */
    pendiente: 'Por anunciar',
  },
  volver: {
    label: 'Ver la gira completa',
  },

  /**
   * VÍDEO DE LA CONVIVENCIA ANTERIOR — encargo del cliente (12-sep-2026):
   * «un botón exactamente aquí del mismo color al original tuyo que lleve a la
   * visualización de este vídeo. Es un vídeo de 3 minutos que visualiza la
   * experiencia de la convivencia anterior que realicé en el mismo hotel».
   *
   * Material entregado por el cliente, grabado por él en Palmaïa. NO se afirma
   * el año ni el número de asistentes: no los dio, y ADR-003 §1 prohíbe
   * rellenar ese hueco con un dato plausible. Lo que sí consta y se puede
   * decir es que fue en el MISMO hotel, que es justo lo que aporta al que duda.
   */
  video: {
    label: 'Retiro anterior en Palmaïa',
    titulo: 'Convivencia anterior en Palmaïa',
    /* El `<video>` no tiene `alt`. Esto es lo que se anuncia en su lugar. */
    descripcion:
      'Vídeo de la convivencia anterior de Azrahel del Mayor en el Hotel Palmaïa: recorrido por el hotel, la playa y los espacios donde se celebra el retiro, con comentarios del propio Azrahel.',
    /**
     * GUID en la librería de Bunny Stream del cliente (`EventoAzrahel`, 750890).
     * No es un secreto: viaja en la URL del reproductor, que es pública. El
     * fichero NO está en este repo a propósito — ver `VideoDialog`.
     */
    bunnyId: 'b0855796-6a6e-40af-81d2-679944c6c3f2',
  },
} as const;
